import { startTransition, useCallback, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
  OrganizationMemberRoleName,
  ReportableCampaignStatusName,
  ReportCampaignListResponse,
  ReportCampaignOptionsResponse,
  ReportCampaignPageSize,
  ReportCampaignSortName,
} from "@multi-chat/shared";
import { getReportCampaignsOptions, listReportCampaigns } from "../../api/reports-api.js";
import {
  campaignReportHref,
  clearCampaignFilters,
  hasActiveCampaignFilters,
  parseCampaignReportQuery,
  type CampaignReportQuery,
} from "./campaign-report-query.js";

const SEARCH_DEBOUNCE_MS = 280;

export function useCampaignReport(input: {
  organizationId: number;
  role: OrganizationMemberRoleName;
}): {
  query: CampaignReportQuery;
  list: ReportCampaignListResponse | null;
  options: ReportCampaignOptionsResponse | null;
  loading: boolean;
  error: unknown;
  filtersActive: boolean;
  canAccess: boolean;
  dateFrom: string;
  dateTo: string;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  setStatus: (status: ReportableCampaignStatusName | undefined) => void;
  setEndpointId: (endpointId: number | undefined) => void;
  setTemplate: (template: string | undefined) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: ReportCampaignPageSize) => void;
  setSort: (sort: ReportCampaignSortName) => void;
  resetFilters: () => void;
  refresh: () => void;
} {
  const canAccess = input.role === "OWNER" || input.role === "ADMIN";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = useMemo(
    () => parseCampaignReportQuery(searchParams.toString()),
    [searchParams],
  );

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [debouncedSearch, setDebouncedSearch] = useState(query.search);
  const [list, setList] = useState<ReportCampaignListResponse | null>(null);
  const [options, setOptions] = useState<ReportCampaignOptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setSearchDraft(query.search);
    setDebouncedSearch(query.search);
  }, [query.search]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchDraft.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchDraft]);

  const replaceQuery = useCallback(
    (next: CampaignReportQuery) => {
      navigate(campaignReportHref(next), { replace: true });
    },
    [navigate],
  );

  useEffect(() => {
    if (debouncedSearch === query.search) return;
    replaceQuery({ ...query, search: debouncedSearch, page: 1 });
  }, [debouncedSearch, query, replaceQuery]);

  const load = useEffectEvent(async (signal: AbortSignal) => {
    if (!canAccess) return;
    setLoading(true);
    setError(null);
    try {
      const apiQuery = {
        fromDate: query.from,
        toDate: query.to,
        search: debouncedSearch || undefined,
        status: query.status,
        endpointId: query.endpointId,
        template: query.template,
        page: query.page,
        pageSize: query.pageSize,
        sort: query.sort,
      };
      const [listResponse, optionsResponse] = await Promise.all([
        listReportCampaigns(input.organizationId, apiQuery, signal),
        getReportCampaignsOptions(
          input.organizationId,
          { fromDate: query.from, toDate: query.to },
          signal,
        ),
      ]);
      if (signal.aborted) return;
      setList(listResponse);
      setOptions(optionsResponse);
    } catch (err) {
      if (signal.aborted) return;
      setError(err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  });

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [
    canAccess,
    input.organizationId,
    query.from,
    query.to,
    query.status,
    query.endpointId,
    query.template,
    query.page,
    query.pageSize,
    query.sort,
    debouncedSearch,
    refreshToken,
  ]);

  return {
    query,
    list,
    options,
    loading,
    error,
    filtersActive: hasActiveCampaignFilters(query),
    canAccess,
    dateFrom: list?.fromDate ?? query.from ?? "",
    dateTo: list?.toDate ?? query.to ?? "",
    setSearch: (search) => setSearchDraft(search),
    setDateRange: (from, to) => replaceQuery({ ...query, from, to, page: 1 }),
    setStatus: (status) => replaceQuery({ ...query, status, page: 1 }),
    setEndpointId: (endpointId) => replaceQuery({ ...query, endpointId, page: 1 }),
    setTemplate: (template) => replaceQuery({ ...query, template, page: 1 }),
    setPage: (page) => replaceQuery({ ...query, page }),
    setPageSize: (pageSize) => replaceQuery({ ...query, pageSize, page: 1 }),
    setSort: (sort) => replaceQuery({ ...query, sort, page: 1 }),
    resetFilters: () => {
      startTransition(() => {
        setSearchDraft("");
        setDebouncedSearch("");
        replaceQuery(clearCampaignFilters(query));
      });
    },
    refresh: () => setRefreshToken((token) => token + 1),
  };
}
